import { useState, useEffect } from "react";
import axios from "axios";
import api from "../../lib/api";
import { toast } from "sonner";
import { X, ChatCircle, ClockClockwise, Paperclip, User, Users, Warning, CheckCircle } from "@phosphor-icons/react";
import { API } from "../../App";
import TaskConversationTab from "./TaskConversationTab";
import TaskAuditTab from "./TaskAuditTab";
import TaskAttachmentsTab from "./TaskAttachmentsTab";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "assignees", label: "Assignees" },
  { key: "conversation", label: "Conversation", icon: ChatCircle },
  { key: "audit", label: "Audit Log", icon: ClockClockwise },
  { key: "attachments", label: "Proof", icon: Paperclip },
];

const fmtDue = (task) => {
  if (!task?.due_date) return "—";
  const d = new Date(`${task.due_date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return task.due_date;
  const datePart = `${String(d.getDate()).padStart(2, "0")} ${d.toLocaleString("en-IN", { month: "short" })} ${d.getFullYear()}`;
  if (!task.due_time) return datePart;
  const [hh, mm] = String(task.due_time).split(":");
  const h = Number(hh); const ampm = h >= 12 ? "PM" : "AM"; const h12 = ((h % 12) || 12);
  return `${datePart} · ${h12}:${mm} ${ampm}`;
};

const STATUS_PILL = {
  pending: { label: "Pending", bg: "#F59E0B15", color: "#F59E0B" },
  in_progress: { label: "In Progress", bg: "#002FA715", color: "#002FA7" },
  completed: { label: "Completed", bg: "#10B98115", color: "#10B981" },
  cancelled: { label: "Cancelled", bg: "#94A3B815", color: "#475569" },
};

export default function TaskDetailDrawer({ open, taskId, user, onClose, onChanged, initialTab = "overview" }) {
  const [task, setTask] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [forceCloseOpen, setForceCloseOpen] = useState(false);
  const [forceReason, setForceReason] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);

  // Sync tab when caller requests a specific landing tab (e.g. Reply → Conversation)
  useEffect(() => {
    if (open) setTab(initialTab || "overview");
  }, [open, taskId, initialTab]);

  const canForceClose = ["admin", "manager", "bdm", "md"].includes(user?.role);

  useEffect(() => {
    if (!open || !taskId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          requester_role: user?.role || "",
          requester_name: user?.name || "",
          requester_user_id: user?.user_id || "",
        });
        const res = await api.get(`${API}/crm/tasks?${params.toString()}`);
        const match = (res.data || []).find((t) => t.task_id === taskId);
        if (!cancelled) setTask(match || null);
      } catch (e) { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, taskId, user?.role, user?.name, user?.user_id]);

  if (!open) return null;

  const updateMyAssigneeStatus = async (newStatus, remarks = "") => {
    if (!task) return;
    try {
      await api.put(`${API}/crm/tasks/${task.task_id}/assignees/${user.user_id}/status`, {
        status: newStatus, remarks,
        requester_user_id: user?.user_id || "",
        requester_role: user?.role || "",
      });
      toast.success("Your status updated");
      onChanged && onChanged();
      // Refresh local task
      const params = new URLSearchParams({
        requester_role: user?.role || "", requester_name: user?.name || "", requester_user_id: user?.user_id || "",
      });
      const res = await api.get(`${API}/crm/tasks?${params.toString()}`);
      setTask((res.data || []).find((t) => t.task_id === task.task_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    }
  };

  // Phase 2A — Single-user task completion. Goes through existing PUT /crm/tasks
  // which already stamps completed_at on status flip. Available to the assigned
  // user OR admin/manager/bdm/md (RBAC enforced at backend).
  const completeSingleTask = async () => {
    if (!task) return;
    try {
      await api.put(`${API}/crm/tasks/${task.task_id}`, {
        status: "completed",
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
        requester_role: user?.role || "",
      });
      toast.success("Task marked complete");
      onChanged && onChanged();
      const params = new URLSearchParams({
        requester_role: user?.role || "", requester_name: user?.name || "", requester_user_id: user?.user_id || "",
      });
      const res = await api.get(`${API}/crm/tasks?${params.toString()}`);
      setTask((res.data || []).find((t) => t.task_id === task.task_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to complete task");
    }
  };

  const submitForceClose = async () => {
    if (!forceReason.trim()) { toast.error("Reason is required"); return; }
    try {
      await api.post(`${API}/crm/tasks/${task.task_id}/force-close`, {
        reason: forceReason.trim(),
        requester_role: user?.role || "",
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
      });
      toast.success("Task force-closed");
      setForceCloseOpen(false); setForceReason("");
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to force-close");
    }
  };

  const myAssignee = task?.task_assignees?.find?.((a) => a.user_id === user?.user_id);
  const isMultiAssignee = (task?.task_assignees?.length || 0) > 0;
  const isSingleAssignee = !isMultiAssignee;
  const amIAssignedSingle = isSingleAssignee && (task?.assigned_to_id === user?.user_id || task?.assigned_to === user?.name);
  const canCompleteSingle = isSingleAssignee && task?.status !== "completed" && task?.status !== "cancelled" && (amIAssignedSingle || canForceClose);

  return (
    <div data-testid="task-detail-drawer" className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} aria-label="Close drawer"></div>
      <aside className="w-full max-w-[640px] bg-white border-l border-slate-200 shadow-xl flex flex-col">
        <header className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Task Details</h2>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{task?.task_id || ""}</p>
          </div>
          <button data-testid="task-drawer-close" onClick={onClose} className="p-2 hover:bg-slate-100 rounded-none" aria-label="Close"><X size={18} /></button>
        </header>

        <div className="border-b border-slate-200 px-2 flex overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon || null;
            const active = tab === t.key;
            const showUnread = t.key === "conversation" && unreadCount > 0;
            return (
              <button
                key={t.key}
                data-testid={`task-drawer-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-3 py-3 text-xs uppercase tracking-wider font-bold border-b-2 transition-colors whitespace-nowrap inline-flex items-center gap-1 hover:text-[#002FA7] ${
                  active ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-slate-500"
                }`}
              >
                {Icon && <Icon size={12} />}
                {t.label}
                {showUnread && (
                  <span data-testid="task-drawer-unread-badge" className="ml-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[9px] tracking-normal font-bold bg-red-600 text-white rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && <div className="text-sm text-slate-400">Loading…</div>}
          {!loading && !task && <div className="text-sm text-slate-400">Task not found or no access.</div>}
          {task && tab === "overview" && (
            <div className="space-y-4" data-testid="task-drawer-overview">
              <div>
                <h3 className="text-2xl font-bold leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>{task.title}</h3>
                {task.description && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{task.description}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Field label="Due"><span data-testid="task-drawer-due">{fmtDue(task)}</span>{task.is_overdue && <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold bg-red-50 text-red-700 border border-red-200"><Warning size={10} weight="bold" /> Overdue</span>}</Field>
                <Field label="Priority"><span className="font-mono text-xs px-1.5 py-0.5 text-white" style={{ background: { P0: "#DC2626", P1: "#F59E0B", P2: "#002FA7", P3: "#64748B" }[task.priority_grade] || "#64748B" }}>{task.priority_grade}</span></Field>
                <Field label="Status">{(() => { const p = STATUS_PILL[task.status] || STATUS_PILL.pending; return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold" style={{ backgroundColor: p.bg, color: p.color }}>{p.label}</span>; })()}</Field>
                <Field label="Created by"><span data-testid="task-drawer-created-by">{task.created_by || task.assigned_by_name || "—"}</span></Field>
                <Field label="Assigned by">{task.assigned_by_name || "—"}</Field>
                <Field label="Related"><span data-testid="task-drawer-related">{task.related_module ? `${task.related_module}${task.related_id ? " · " + task.related_id : ""}` : "—"}</span></Field>
              </div>
              {task.force_closed_at && (
                <div className="border border-amber-300 bg-amber-50 p-3 text-xs" data-testid="task-drawer-force-close-note">
                  <div className="uppercase tracking-wider font-bold text-amber-700">Force-closed</div>
                  <div className="text-amber-800 mt-1">{task.force_closed_reason}</div>
                  <div className="text-amber-600 mt-1">By {task.force_closed_by} · {task.force_closed_at.slice(0, 16).replace("T", " ")}</div>
                </div>
              )}
              {/* Single-assignee: Complete Task button. Multi-assignee tasks use per-user "Mark My Part Complete" in Assignees tab. */}
              {canCompleteSingle && (
                <button
                  data-testid="complete-task-btn"
                  onClick={completeSingleTask}
                  className="text-xs uppercase tracking-wider font-bold px-4 py-2 bg-[#10B981] text-white hover:bg-emerald-700 inline-flex items-center gap-1"
                >
                  <CheckCircle size={12} weight="bold" /> Complete Task
                </button>
              )}
              {canForceClose && task.status !== "completed" && isMultiAssignee && (
                <div data-testid="force-close-block">
                  {!forceCloseOpen ? (
                    <button data-testid="force-close-open" onClick={() => setForceCloseOpen(true)} className="text-xs uppercase tracking-wider font-bold px-3 py-2 border border-amber-300 text-amber-700 hover:bg-amber-50">
                      Force Close Task
                    </button>
                  ) : (
                    <div className="border border-amber-300 p-3 space-y-2">
                      <label className="text-xs uppercase tracking-wider font-bold text-amber-700 block">Reason (required)</label>
                      <textarea data-testid="force-close-reason" value={forceReason} onChange={(e) => setForceReason(e.target.value)} rows={2} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
                      <div className="flex gap-2">
                        <button data-testid="force-close-submit" onClick={submitForceClose} className="text-xs uppercase tracking-wider font-bold px-3 py-2 bg-amber-600 text-white hover:bg-amber-700">Force Close</button>
                        <button onClick={() => { setForceCloseOpen(false); setForceReason(""); }} className="text-xs uppercase tracking-wider font-bold px-3 py-2 border border-slate-300 hover:bg-slate-50">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {task && tab === "assignees" && (
            <div className="space-y-3" data-testid="task-drawer-assignees">
              {isSingleAssignee ? (
                <div className="border border-slate-200 p-3" data-testid="single-assignee-block">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold inline-flex items-center gap-1">
                        <User size={12} /> {task.assigned_to || "Unassigned"}
                        {amIAssignedSingle && <span className="text-[9px] uppercase tracking-wider text-[#002FA7] font-bold ml-1">(YOU)</span>}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">SINGLE ASSIGNEE</div>
                    </div>
                    {(() => { const p = STATUS_PILL[task.status] || STATUS_PILL.pending; return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold" style={{ backgroundColor: p.bg, color: p.color }}>{p.label}</span>; })()}
                  </div>
                  {task.completed_at && <div className="text-[11px] text-slate-500 mt-2 inline-flex items-center gap-1"><CheckCircle size={11} weight="fill" className="text-emerald-600" /> Completed {task.completed_at.slice(0, 16).replace("T", " ")}</div>}
                  {canCompleteSingle && (
                    <div className="mt-3">
                      <button data-testid="complete-task-btn-assignees" onClick={completeSingleTask} className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 bg-[#10B981] text-white hover:bg-emerald-700 inline-flex items-center gap-1">
                        <CheckCircle size={10} weight="bold" /> Complete Task
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                task.task_assignees.map((a) => {
                  const p = STATUS_PILL[a.status] || STATUS_PILL.pending;
                  const isMe = a.user_id === user?.user_id;
                  return (
                    <div key={a.user_id} className="border border-slate-200 p-3" data-testid={`assignee-row-${a.user_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold inline-flex items-center gap-1"><User size={12} /> {a.user_name} {isMe && <span className="text-[9px] uppercase tracking-wider text-[#002FA7] font-bold ml-1">(YOU)</span>}</div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{(a.role || "").toUpperCase()}</div>
                        </div>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold" style={{ backgroundColor: p.bg, color: p.color }}>{p.label}</span>
                      </div>
                      {a.completed_at && <div className="text-[11px] text-slate-500 mt-2 inline-flex items-center gap-1"><CheckCircle size={11} weight="fill" className="text-emerald-600" /> Completed {a.completed_at.slice(0, 16).replace("T", " ")}</div>}
                      {a.remarks && <div className="text-xs text-slate-600 mt-1 italic">"{a.remarks}"</div>}
                      {isMe && a.status !== "completed" && (
                        <div className="mt-3 flex gap-2">
                          <button data-testid="my-mark-in-progress" onClick={() => updateMyAssigneeStatus("in_progress")} className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5">Mark In Progress</button>
                          <button data-testid="my-mark-completed" onClick={() => updateMyAssigneeStatus("completed")} className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 bg-[#10B981] text-white hover:bg-emerald-700">Mark My Part Complete</button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
          {task && tab === "conversation" && (
            <TaskConversationTab taskId={task.task_id} user={user} onUnreadChange={setUnreadCount} />
          )}
          {task && tab === "audit" && (
            <TaskAuditTab taskId={task.task_id} user={user} />
          )}
          {task && tab === "attachments" && (
            <TaskAttachmentsTab taskId={task.task_id} task={task} user={user} />
          )}
        </div>
      </aside>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold mb-1">{label}</div>
      <div className="text-sm text-slate-700">{children}</div>
    </div>
  );
}
